import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';

export type RuleClassification = 'pnl' | 'unknown' | 'skip';
export type RuleTextOp = 'equals' | 'contains' | 'matches';

export interface RuleTextCondition {
  type: 'text';
  field: 'description';
  op: RuleTextOp;
  value: string;
}

export interface RuleTransactionTypeCondition {
  type: 'transaction_type';
  op: 'equals';
  value: 'Credit' | 'Debit';
}

export type RuleCondition = RuleTextCondition | RuleTransactionTypeCondition;

export interface RuleApplies {
  classification: RuleClassification;
  monthly_finance_type_key?: string;
  address_id?: number;
}

export interface FinancialTransactionRule {
  id: number;
  position: number;
  conditions: RuleCondition[];
  applies: RuleApplies;
  created_by: 'llm' | 'customer' | 'admin';
  created_at: string;
  updated_at: string;
}

export interface FinancialTransactionRuleBody {
  conditions: RuleCondition[];
  applies: RuleApplies;
}

/** The minimum a bank row needs for a client-side match preview. */
export interface RuleMatchableRow {
  description: string;
  transaction_type: 'Credit' | 'Debit';
}

@Injectable({ providedIn: 'root' })
export class FinancialTransactionRulesService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api_url}api/v1/customer/financial-transaction-rules`;

  getRules(): Promise<FinancialTransactionRule[]> {
    return firstValueFrom(
      this.http.get<FinancialTransactionRule[]>(this.baseUrl)
    );
  }

  createRule(body: FinancialTransactionRuleBody): Promise<FinancialTransactionRule> {
    return firstValueFrom(
      this.http.post<FinancialTransactionRule>(this.baseUrl, body)
    );
  }

  updateRule(id: number, body: Partial<FinancialTransactionRuleBody>): Promise<FinancialTransactionRule> {
    return firstValueFrom(
      this.http.put<FinancialTransactionRule>(`${this.baseUrl}/${id}`, body)
    );
  }

  reorderRules(ids: number[]): Promise<FinancialTransactionRule[]> {
    return firstValueFrom(
      this.http.put<FinancialTransactionRule[]>(`${this.baseUrl}/reorder`, { ids })
    );
  }

  deleteRule(id: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${this.baseUrl}/${id}`)
    );
  }

  /**
   * Mirrors the backend matcher (all conditions must match, text ops are
   * case-insensitive) so the rule form can preview matches without a round trip.
   */
  matches(row: RuleMatchableRow, conditions: RuleCondition[]): boolean {
    const usable = conditions.filter(c => this.isConditionComplete(c));
    if (usable.length === 0) { return false; }
    return usable.every(condition => this.matchesCondition(row, condition));
  }

  isConditionComplete(condition: RuleCondition): boolean {
    if (!condition?.value) { return false; }
    if (condition.type === 'text' && condition.op === 'matches') {
      return this.regexCompiles(condition.value);
    }
    return true;
  }

  private matchesCondition(row: RuleMatchableRow, condition: RuleCondition): boolean {
    if (condition.type === 'transaction_type') {
      return row.transaction_type === condition.value;
    }
    const text = (row.description || '').toString();
    const value = condition.value.toString();
    switch (condition.op) {
      case 'equals': return text.toLowerCase() === value.toLowerCase();
      case 'contains': return text.toLowerCase().includes(value.toLowerCase());
      case 'matches': return this.regexCompiles(value) && new RegExp(value, 'i').test(text);
      default: return false;
    }
  }

  private regexCompiles(value: string): boolean {
    try {
      new RegExp(value, 'i');
      return true;
    } catch {
      return false;
    }
  }
}
